<template>
  <div class="paycools-kyc-page">
    <!-- 左侧步骤栏 -->
    <aside class="kyc-sidebar">
      <ul class="kyc-step-list">
        <li
          v-for="(step, index) in steps"
          :key="step.value"
          class="kyc-step-item"
          :class="{ 'is-active': activeStep === index, 'is-disabled': isStepDisabled(index) }"
          @click="handleStepClick(index)"
        >
          <span class="kyc-step-index">{{ index + 1 }}.</span>
          <span class="kyc-step-label">{{ step.label }}</span>
        </li>
      </ul>
    </aside>

    <!-- header is rendered inside each step component; back button moved to child headers -->

    <!-- 右侧内容区域：根据步骤切换 -->
    <section class="kyc-content">
      <keep-alive>
        <component
          :is="currentComponent"
          ref="stepComponent"
          :kyc-data="kycData"
          :company-files="companyFiles"
          :loading="loading"
          :submitting="submitting"
          @next="handleNext"
          @submit="handleSubmit"
          @requestBack="onBack"
          @firstStepSaved="firstStepSaved = true"
          @saved="fetchKycData"
          @markChanged="markChanged"
          @resetChanged="resetChanged"
        />
      </keep-alive>
    </section>

    <!-- 退出确认弹窗（保存草稿提示） -->
    <el-dialog
      :title="$t('kyc.exit_dialog_title')"
      :visible.sync="showExitDialog"
      width="600px"
      :close-on-click-modal="false"
      :show-close="true"
    >
      <div style="font-size:16px;color:#243b55;">
        {{ $t('kyc.exit_with_unsaved') }}
      </div>
      <span slot="footer" class="dialog-footer" style="display:flex;gap:16px;justify-content:flex-end;">
        <el-button @click="showExitDialog = false">{{ $t('kyc.cancel') }}</el-button>
        <el-button type="primary" @click="saveAllAndNavigateHome">{{ $t('kyc.save') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import KycCompanyInfo from './KycCompanyInfo.vue'
import KycCompanyDocument from './KycCompanyDocument.vue'
import KycSignatoryInfo from './KycSignatoryInfo.vue'
import KycAccountInfo from './KycAccountInfo.vue'
import { getMerchantKycInfo, submitKyc } from '@/api/kyc/kyc'

export default {
  name: 'PaycoolsKycIndex',
  components: {
    KycCompanyInfo,
    KycCompanyDocument,
    KycSignatoryInfo,
    KycAccountInfo
  },
  data() {
    return {
      activeStep: 0,
      stepKeys: [
        'companyInfo',
        'companyDocument',
        'signatoryInfo',
        'accountInfo'
      ],
      hasChanged: false,
      kycData: null, // 存储KYC数据
      loading: false,
      showExitDialog: false,
      firstStepSaved: false, // 跟踪第一步是否已保存
      companyFiles: [], // 存储所有步骤的公司文件
      submitting: false // 全局提交状态，用于禁用按钮
    }
  },
  computed: {
    steps() {
      const stepMap = {
        companyInfo: 'steps_company_info',
        companyDocument: 'steps_company_document',
        signatoryInfo: 'steps_signatory_info',
        accountInfo: 'steps_account_info'
      }
      return this.stepKeys.map(key => ({
        value: key,
        label: this.$t(`kyc.${stepMap[key]}`)
      }))
    },
    currentComponent() {
      switch (this.activeStep) {
        case 0:
          return 'KycCompanyInfo'
        case 1:
          return 'KycCompanyDocument'
        case 2:
          return 'KycSignatoryInfo'
        case 3:
        default:
          return 'KycAccountInfo'
      }
    }
  },
  created() {
    if (this.$store.getters.isMX) {
      this.$router.push('/login')
    }
    const kycStatus = this.$store.getters.kycStatus
    if (kycStatus !== 1 && kycStatus !== 4) {
      this.$router.replace('/dashboard')
    }
    this.beforeUnloadHandler = (event) => {
      if (!this.hasChanged) return
      event.preventDefault()
      event.returnValue = true
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    this.$store.dispatch('user/getMerchantList')
    this.fetchKycData()
  },
  beforeDestroy() {
    document.removeEventListener('beforeunload', this.beforeUnloadHandler)
  },
  methods: {
    markChanged() {
      this.hasChanged = true
    },
    resetChanged() {
      this.hasChanged = false
    },
    async fetchKycData() {
      try {
        this.loading = true
        const response = await getMerchantKycInfo()
        const data = response.data || {}
        const audit = data.audit_info || {}
        // Only allow entry for unverified (status === 1) or rejected (status === 4)
        if (audit && audit.kyc_status !== 1 && audit.kyc_status !== 4) {
          this.$router.replace('/404')
          return
        }
        this.kycData = data
        // 初始化companyFiles数组，包含所有现有文件
        this.companyFiles = data.company_files || []
      } catch (error) {
        console.error('Failed to fetch KYC data:', error)
        this.$message.error('获取KYC信息失败')
      } finally {
        this.loading = false
      }
    },
    handleStepClick(index) {
      // // 临时调试功能：允许随意跳转页面
      // this.activeStep = index
      // return

      // 如果点击的是第一步，允许跳转
      if (index === 0) {
        this.activeStep = index
        return
      }

      // 如果第一步已保存或有回显数据，允许跳转到其他步骤
      if (this.firstStepSaved || this.hasFirstStepData()) {
        this.activeStep = index
      }
      // 否则不做任何操作，直接禁用点击
    },
    isStepDisabled(index) {
      // // 临时调试功能：所有步骤都可用
      // return false

      // 第一步始终可用
      if (index === 0) return false
      // 其他步骤只有在第一步已保存或有回显数据时才可用
      return !(this.firstStepSaved || this.hasFirstStepData())
    },
    hasFirstStepData() {
      // 检查是否有第一步的回显数据
      if (!this.kycData) return false

      const companyBasicInfo = this.kycData.company_basic_info
      const companyBusinessInfo = this.kycData.company_business_info

      // 只需要检查对应的object是否有值即可
      // 因为只有全部值填写完成并且通过校验之后才可以提交
      return !!(companyBasicInfo && Object.keys(companyBasicInfo).length > 0) ||
        !!(companyBusinessInfo && Object.keys(companyBusinessInfo).length > 0)
    },
    onBack() {
      // show confirmation dialog before leaving
      this.showExitDialog = true
    },
    async saveAllAndNavigateHome() {
      this.showExitDialog = false
      let errorMessageShown = false // 标记是否已显示错误提示

      // iterate all steps and call saveDraft if available
      for (let i = 0; i < this.stepKeys.length; i++) {
        await this.$nextTick()

        // wait until child component mounts and exposes saveDraft (timeout after 2s)
        const waitForReady = (checkFn, timeout = 2000) => {
          const interval = 50
          let elapsed = 0
          return new Promise((resolve) => {
            const timer = setInterval(() => {
              if (checkFn()) {
                clearInterval(timer)
                resolve(true)
              } else {
                elapsed += interval
                if (elapsed >= timeout) {
                  clearInterval(timer)
                  resolve(false)
                }
              }
            }, interval)
          })
        }

        try {
          const ready = await waitForReady(() => {
            const stepComponent = this.$refs.stepComponent
            return stepComponent && (typeof stepComponent.saveDraft === 'function' || (stepComponent.$emit && stepComponent.saveDraft === undefined))
          }, 2000)

          const stepComponent = this.$refs.stepComponent
          if (ready && stepComponent) {
            if (typeof stepComponent.saveDraft === 'function') {
              await stepComponent.saveDraft()
            } else if (stepComponent.$emit) {
              stepComponent.$emit('save')
            }
          } else {
            // last resort: emit save on child ref if exists
            if (stepComponent && stepComponent.$emit) stepComponent.$emit('save')
          }
        } catch (err) {
          // 只弹一次错误提示
          if (!errorMessageShown) {
            errorMessageShown = true
            this.$message.error(this.$t('kyc.validation_all_fields_required') || 'Please fill in all required fields')
          }
          console.error('saveDraft failed for step', i, err)
        }
      }
      // 不管是否验证通过，都返回首页
      this.$router.push({ path: '/' })
    },
    handleNext() {
      // 前三步的"下一步"按钮：跳转到下一页
      if (this.activeStep < 3) {
        this.activeStep++
      }
    },
    async handleSubmit() {
      this.showExitDialog = false
      this.submitting = true
      const originalStep = this.activeStep
      // iterate all steps and call saveDraft if available
      for (let i = 0; i < this.stepKeys.length; i++) {
        // 先切换到对应步骤
        this.activeStep = i
        // 等待组件渲染
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 100))

        try {
          const stepComponent = this.$refs.stepComponent
          if (stepComponent && typeof stepComponent.saveDraft === 'function') {
            await stepComponent.saveDraft()
          }
        } catch (err) {
          // 保存失败，阻止后续提交
          this.submitting = false
          this.$message.error(this.$t('kyc.validation_all_fields_required') || 'Please fill in all required fields')
          this.activeStep = originalStep
          return
        }
      }
      // 只有所有步骤保存成功才继续提交
      this.activeStep = originalStep
      try {
        // 调用提交接口（不强制保存当前步骤，因为用户应该已经保存过了）
        const res = await submitKyc()
        if (res.code === 10000) {
          this.$message.success(this.$t('kyc.submit_success') || '提交成功')
          // 刷新用户信息以更新KYC状态
          await this.$store.dispatch('user/getInfo')
          // 可以在这里添加跳转逻辑，比如跳转到成功页面或首页
          this.$router.push('/dashboard') // 或者其他合适的页面
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.paycools-kyc-page {
  display: flex;
  align-items: stretch;
  background-color: #f5f7fb;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
}

.kyc-sidebar {
  width: 240px;
  background-color: #f7f8fc;
  border-radius: 8px;
  padding: 24px 0;
  box-shadow: 0 0 0 1px #edf0f7;
}

.kyc-step-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.kyc-step-item {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  font-size: 14px;
  color: #7b8192;
  cursor: pointer;
  user-select: none;
}

.kyc-step-item.is-active {
  background-color: #ffffff;
  color: #2f3a60;
  font-weight: 600;
  border-left: 3px solid #3b82f6;
}

.kyc-step-item.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
  opacity: 0.6;
}

.kyc-step-index {
  margin-right: 8px;
}

.kyc-step-label {
  flex: 1;
}

.kyc-content {
  flex: 1;
}
</style>

